// @flow
import React, { useCallback, useEffect, useState } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import gql from 'graphql-tag';
import { sanitizeRouteParams } from 'metaup/routing/routingUtils';
import type { Routes } from 'metaup/routing/routingUtils';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import ErrorCapsule from '../core/exceptions/ErrorCapsule';

import DevicesListPageView from './views/DevicesListPageView';
import type { Device } from './model/Device.model';
import { apolloMutate, apolloQuery } from '../core/data/apolloClient';
import type { Brand } from './model/Brand.model';
import { getAuthUser } from '../auth/authState';
import FolderUploadDialog from './FolderUploadDialog';
import { filesInsideTop, planLekala, splitTopFolders } from './folderUpload';

type DevicesListState = {
  devices: null | Array<Device> | ErrorCapsule,
  brand: ?Brand,
}

const deviceShape = `{
  id
  deviceType { id name }
  name
  preview {
    id
    fit(width: 100, height: 100) { width height url }
  }
}`;

const initialState: DevicesListState = {
  devices: null,
  brand: null,
};

const ACTION_SET_DEVICES = 'library/devicesList/ACTION_SET_DEVICES';
const ACTION_SET_BRAND = 'library/devicesList/ACTION_SET_BRAND';
const ACTION_DELETE_DEVICE = 'library/devicesList/ACTION_DELETE_DEVICE';

function setDevices(devices: null | Array<Device> | ErrorCapsule) {
  return {
    type: ACTION_SET_DEVICES,
    devices,
  };
}

function setBrand(brand: ?Brand) {
  return {
    type: ACTION_SET_BRAND,
    brand,
  };
}

async function loadDevices(brandId: string) {
  try {
    const brand = await apolloQuery(gql` query ($id: String!) {
      getBrand(id: $id) {
        id
        name
        devices(filter: { _sort: "name" }) ${deviceShape}
      }
    } `, {
      id: brandId,
    });

    return [
      setBrand(_omit(brand, ['devices'])),
      setDevices(brand.devices),
    ];
  } catch (err) {
    return setDevices(new ErrorCapsule(err, () => [
      setBrand(initialState.brand),
      setDevices(initialState.devices),
      loadDevices(brandId),
    ]));
  }
}

async function deleteDevice(id) {
  await apolloMutate(
    gql`
      mutation ($id: String!) {
        deleteDevice(id: $id)
      }
    `,
    { id }
  );

  return {
    type: ACTION_DELETE_DEVICE,
    id,
  };
}

export function devicesListReducer(
  state: DevicesListState = initialState,
  action: AnyAction
) {
  switch (action.type) {

    case ACTION_SET_DEVICES:
      return {
        ...state,
        devices: action.devices,
      };

    case ACTION_SET_BRAND:
      return {
        ...state,
        brand: action.brand,
      };

    case ACTION_DELETE_DEVICE:
      return {
        ...state,
        devices: _filter(state.devices, ({ id }) => id !== action.id),
      };

    default:
      return state;
  }
}

type Props = {
  brandId: string,
}

// Папка, перетащенная или выбранная на странице бренда, — это модель: окно
// спросит название (по умолчанию имя папки) и тип, потом загрузит лекала.
// Несколько папок сразу — несколько моделей, окна по очереди.
function DevicesListPage({ brandId }: Props) {
  const dispatch = useDispatch();
  const { brand, devices } = useSelector(({ library }) => library.devicesList);
  const user = useSelector(getAuthUser);
  const [queue, setQueue] = useState([]);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    dispatch(loadDevices(brandId));
  }, [brandId]);

  const openFolders = files => {
    const { folders, loose } = splitTopFolders(files);
    const next = [];
    const empty = [];
    folders.forEach((list, folderName) => {
      const plan = planLekala(filesInsideTop(list));
      if (plan.items.length) next.push({ folderName, plan });
      else empty.push(`«${folderName}»`);
    });
    const notes = [];
    if (!folders.size) notes.push('Перетащите папку модели целиком: внутри лекала .prn и картинки .jpg, .png.');
    else if (loose.length) notes.push(`Файлы вне папок пропущены: ${loose.length}.`);
    if (empty.length) notes.push(`В папке ${empty.join(', ')} нет лекал: файлов .prn не нашлось.`);
    setNotice(notes.length ? notes.join(' ') : null);
    if (next.length) setQueue(next);
  };
  const onDrop = useCallback(files => openFolders(files), []);
  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setNotice(null),
    noClick: true,
    noKeyboard: true,
    disabled: Boolean(user && user.isManager),
  });

  const current = queue[0];
  return (
    <>
      <DevicesListPageView
        brand={brand}
        devices={devices}
        rootProps={getRootProps()}
        isDragActive={isDragActive}
        notice={notice}
        onDeleteDevice={(id) => dispatch(deleteDevice(id))}
        onFolderFiles={openFolders}
      />
      {current && brand && Array.isArray(devices) && (
        <FolderUploadDialog
          key={current.folderName}
          mode="brand"
          brand={brand}
          devices={devices}
          folderName={current.folderName}
          plan={current.plan}
          onClose={changed => {
            if (changed) dispatch(loadDevices(brandId));
            setQueue(rest => rest.slice(1));
          }}
        />
      )}
    </>
  );
}

export function devicesListPageRoutes(): Routes {
  return [
    {
      title: 'Модели',
      path: '/devices/:brandId/',
      isEnabled: ({ isUser }) => isUser,
      render: params => (
        <DevicesListPage
          {...sanitizeRouteParams(params, {
            brandId: 'id',
          })}
        />
      ),
      design: null, // eslint-disable-line global-require
    },
  ];
}
